import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Volume2, VolumeX, Square, RotateCcw, Play, CameraOff, Check, X } from 'lucide-react';
import usePoseDetection from '../hooks/usePoseDetection';
import useHeelRaiseCounter from '../hooks/useHeelRaiseCounter';
import useVoiceFeedback from '../hooks/useVoiceFeedback';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ExercisePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const goal = location.state?.goal || 10;

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const startTimeRef = useRef(null);
  const prevRepCountRef = useRef(0);
  const sessionActiveRef = useRef(false);

  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [sessionActive, setSessionActive] = useState(false);
  const [duration, setDuration] = useState(0);
  const [goalAchieved, setGoalAchieved] = useState(false);

  const { 
    repCount, 
    isInRaise, 
    feedback, 
    processLandmarks, 
    reset, 
    isCalibrated, 
    heelRiseAmount,
    kneeAngle,
    footAngle,
    detectionStatus,
    calibrationState,
    calibrationProgress,
    readyToStart
  } = useHeelRaiseCounter();
  
  const { isEnabled: voiceEnabled, announceRep, announceGoalAchieved, announceEncouragement, announceStart, announceEnd, toggleVoice } = useVoiceFeedback();

  useEffect(() => {
    sessionActiveRef.current = sessionActive;
  }, [sessionActive]);

  const heelRaiseState = {
    isInRaise,
    isCalibrated: isCalibrated(),
    heelRiseAmount,
    kneeAngle,
    footAngle,
    detectionStatus,
    calibrationState
  };

  const handleLandmarksDetected = useCallback((landmarks) => {
    if (sessionActiveRef.current) {
      processLandmarks(landmarks);
    }
  }, [processLandmarks]);

  const { isLoading, error: poseError, start: startPose, stop: stopPose } = usePoseDetection(videoRef, canvasRef, handleLandmarksDetected, heelRaiseState);

  const startCamera = async () => {
    try {
      setCameraError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setCameraActive(true);
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError('Could not access camera. Please allow camera permissions.');
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  };

  const startSession = () => {
    reset();
    setSessionActive(true);
    sessionActiveRef.current = true;
    setGoalAchieved(false);
    startTimeRef.current = Date.now();
    prevRepCountRef.current = 0;
    startPose();
    announceStart();
  };

  const endSession = async () => {
    stopPose();
    setSessionActive(false);
    sessionActiveRef.current = false;
    announceEnd(repCount, goal);

    const durationSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
    try {
      await axios.post(`${API}/sessions`, {
        exercise_type: 'right_leg_calf_raise',
        reps_completed: repCount,
        goal: goal,
        duration_seconds: durationSeconds
      });
    } catch (err) {
      console.error('Failed to save session:', err);
    }

    setTimeout(() => navigate('/history'), 2000);
  };

  const resetExercise = () => {
    reset();
    setGoalAchieved(false);
    startTimeRef.current = Date.now();
    prevRepCountRef.current = 0;
  };

  useEffect(() => {
    if (repCount > prevRepCountRef.current) {
      announceRep(repCount);
      const repsLeft = goal - repCount;
      announceEncouragement(repsLeft);
      if (repCount >= goal && !goalAchieved) {
        setGoalAchieved(true);
        announceGoalAchieved();
      }
      prevRepCountRef.current = repCount;
    }
  }, [repCount, goal, goalAchieved, announceRep, announceEncouragement, announceGoalAchieved]);

  useEffect(() => {
    let interval;
    if (sessionActive && startTimeRef.current) {
      interval = setInterval(() => {
        setDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [sessionActive]);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const progress = Math.min((repCount / goal) * 100, 100);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Detection status indicator component
  const LandmarkIndicator = ({ label, detected }) => (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${detected ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
      {detected ? <Check size={12} /> : <X size={12} />}
      <span className="text-xs font-medium uppercase">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black overflow-hidden">
      <video
        ref={videoRef}
        data-testid="camera-video"
        className="absolute inset-0 w-full h-full object-cover video-mirror"
        playsInline
        muted
      />

      <canvas
        ref={canvasRef}
        data-testid="pose-canvas"
        className="absolute inset-0 w-full h-full object-cover video-mirror pointer-events-none"
      />

      <div className="absolute inset-0 pointer-events-none z-10">
        
        {/* TOP BAR */}
        <div className="absolute top-0 left-0 right-0 pointer-events-auto">
          <div className="glass border-b border-white/10 px-4 py-3">
            <div className="flex items-center justify-between gap-4 max-w-5xl mx-auto">
              <div className="flex items-center gap-2">
                <button
                  data-testid="back-button"
                  onClick={() => { stopPose(); stopCamera(); navigate('/'); }}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                >
                  <ArrowLeft size={24} />
                </button>
                <button
                  data-testid="voice-toggle-button"
                  onClick={toggleVoice}
                  className={`p-2 rounded-lg transition-colors ${voiceEnabled ? 'text-cyan-400 bg-cyan-400/10' : 'text-zinc-500 hover:bg-white/10'}`}
                >
                  {voiceEnabled ? <Volume2 size={20} /> : <VolumeX size={20} />}
                </button>
                <span className="text-xs text-cyan-400 font-bold ml-2">RIGHT LEG ONLY</span>
              </div>

              <div className="flex items-center gap-3">
                {!sessionActive ? (
                  <button
                    data-testid="start-session-button"
                    onClick={startSession}
                    disabled={isLoading || !cameraActive}
                    className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 disabled:bg-zinc-700 disabled:text-zinc-500 text-black font-bold rounded-full transition-transform active:scale-95 neon-cyan flex items-center gap-2"
                  >
                    <Play size={20} fill="currentColor" />
                    START
                  </button>
                ) : (
                  <>
                    <button
                      data-testid="reset-button"
                      onClick={resetExercise}
                      className="p-3 bg-zinc-800 hover:bg-zinc-700 text-white rounded-full transition-colors"
                      title="Recalibrate"
                    >
                      <RotateCcw size={20} />
                    </button>
                    <button
                      data-testid="stop-session-button"
                      onClick={endSession}
                      className="px-8 py-3 bg-red-500 hover:bg-red-400 text-white font-bold rounded-full transition-transform active:scale-95 flex items-center gap-2"
                    >
                      <Square size={18} fill="currentColor" />
                      END
                    </button>
                  </>
                )}
              </div>

              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="font-data text-xl text-white">{formatDuration(duration)}</p>
                  <div className="flex items-center gap-1 text-xs">
                    <span className={`w-2 h-2 rounded-full ${isLoading ? 'bg-yellow-500 animate-pulse' : cameraActive && !poseError ? 'bg-green-500' : 'bg-red-500'}`} />
                    <span className="text-zinc-400">
                      {isLoading ? 'Loading AI...' : cameraActive ? 'Camera Ready' : 'No Camera'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* LEFT - Rep counter */}
        <div className="absolute top-24 left-6 pointer-events-auto">
          <div className="glass rounded-2xl p-6">
            <p className="text-xs tracking-widest uppercase text-zinc-500 font-bold mb-1">Reps</p>
            <p 
              data-testid="rep-counter"
              className={`font-heading text-7xl md:text-8xl font-black text-cyan-400 ${repCount > 0 ? 'rep-pop' : ''}`}
            >
              {repCount}
            </p>
          </div>
        </div>

        {/* RIGHT - Goal progress */}
        <div className="absolute top-24 right-6 pointer-events-auto">
          <div className={`glass rounded-2xl p-6 ${goalAchieved ? 'goal-achieved neon-green' : ''}`}>
            <div className="relative w-28 h-28">
              <svg className="progress-ring w-full h-full" viewBox="0 0 100 100">
                <circle className="stroke-zinc-800" strokeWidth="6" fill="transparent" r="45" cx="50" cy="50" />
                <circle
                  data-testid="progress-circle"
                  className={`progress-ring-circle ${goalAchieved ? 'stroke-green-500' : 'stroke-cyan-500'}`}
                  strokeWidth="6"
                  fill="transparent"
                  r="45" cx="50" cy="50"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="font-data text-2xl font-bold text-white">{Math.round(progress)}%</p>
                <p className="text-xs text-zinc-400">{repCount}/{goal}</p>
              </div>
            </div>
          </div>
        </div>

        {/* LEFT SIDE - Detection Status Panel */}
        {sessionActive && (
          <div className="absolute top-60 left-6 pointer-events-auto">
            <div className="glass rounded-xl p-4 space-y-3">
              <p className="text-xs text-zinc-400 uppercase font-bold mb-2">Right Leg Detection</p>
              <div className="flex flex-wrap gap-2">
                <LandmarkIndicator label="Hip" detected={detectionStatus.hip} />
                <LandmarkIndicator label="Knee" detected={detectionStatus.knee} />
                <LandmarkIndicator label="Ankle" detected={detectionStatus.ankle} />
                <LandmarkIndicator label="Heel" detected={detectionStatus.heel} />
                <LandmarkIndicator label="Toe" detected={detectionStatus.toe} />
              </div>
              
              {/* Calibration Progress */}
              {calibrationState === 'calibrating' && (
                <div className="mt-3">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-yellow-400">Calibrating...</span>
                    <span className="text-yellow-400">{calibrationProgress}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-yellow-500 transition-all duration-200"
                      style={{ width: `${calibrationProgress}%` }}
                    />
                  </div>
                </div>
              )}
              
              {/* Calibration Complete */}
              {calibrationState === 'complete' && (
                <div className="mt-3 p-2 bg-green-500/20 rounded-lg border border-green-500/30">
                  <p className="text-green-400 text-sm font-bold flex items-center gap-2">
                    <Check size={16} />
                    Position Locked!
                  </p>
                  <p className="text-green-400/70 text-xs mt-1">
                    🔒 Tracking right leg only
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* RIGHT SIDE - Metrics Panel */}
        {sessionActive && isCalibrated() && (
          <div className="absolute top-60 right-6 pointer-events-auto">
            <div className="glass rounded-xl p-4 space-y-3 min-w-[140px]">
              <div>
                <p className="text-xs text-zinc-500 uppercase">Foot Angle</p>
                <p className={`font-data text-xl ${footAngle >= 6 ? 'text-green-400' : 'text-orange-400'}`}>
                  {footAngle}°
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">Knee Angle</p>
                <p className={`font-data text-xl ${kneeAngle >= 155 ? 'text-green-400' : 'text-red-400'}`}>
                  {kneeAngle}°
                </p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 uppercase">Heel Rise</p>
                <p className={`font-data text-xl ${heelRiseAmount >= 0.015 ? 'text-green-400' : 'text-orange-400'}`}>
                  {(heelRiseAmount * 100).toFixed(0)}%
                </p>
              </div>
            </div>
          </div>
        )}

        {/* BOTTOM - Feedback */}
        <div className="absolute bottom-6 left-6 right-6 pointer-events-auto">
          <div className="flex flex-col items-center gap-3">
            
            {/* Ready to Start Banner */}
            {sessionActive && readyToStart && calibrationState === 'complete' && !isInRaise && repCount === 0 && (
              <div className="glass rounded-xl px-6 py-3 bg-green-500/20 border border-green-500/30 animate-pulse">
                <p className="text-green-400 font-bold text-lg">
                  🚀 CALIBRATION COMPLETE - START YOUR CALF RAISES NOW!
                </p>
              </div>
            )}
            
            {/* Calibration Instructions */}
            {sessionActive && calibrationState === 'calibrating' && (
              <div className="glass rounded-xl px-6 py-3 bg-yellow-500/20 border border-yellow-500/30">
                <p className="text-yellow-400 font-medium text-center">
                  ⚠️ Keep heel FLAT on floor during calibration
                </p>
              </div>
            )}
            
            <div className="flex items-center justify-center gap-6">
              {/* Feedback message */}
              <div className={`glass rounded-xl px-6 py-4 max-w-xl ${
                calibrationState === 'complete' && !isInRaise && repCount === 0 
                  ? 'border-2 border-green-500/50' 
                  : ''
              }`}>
                <p data-testid="feedback-message" className="text-center text-lg text-white font-medium">
                  {sessionActive ? feedback : 'Stand on RIGHT leg, foot FLAT, side to camera → Press START'}
                </p>
              </div>
              
              {/* Raise indicator */}
              <div className={`glass rounded-xl px-5 py-4 transition-all ${isInRaise ? 'neon-green bg-green-500/20' : 'bg-zinc-800/50'}`}>
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1 text-center">Status</p>
                <p data-testid="raise-status" className={`font-heading text-2xl font-bold text-center ${isInRaise ? 'text-green-400' : 'text-zinc-400'}`}>
                  {isInRaise ? '▲ UP' : '▼ DOWN'}
                </p>
              </div>
            </div>
            
            {!sessionActive && (
              <p className="text-xs text-zinc-500 text-center">
                Stand sideways on your RIGHT leg with foot FLAT on floor for calibration
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Camera error overlay */}
      {cameraError && (
        <div className="absolute inset-0 bg-black/90 flex items-center justify-center z-20">
          <div className="glass rounded-2xl p-8 max-w-md text-center">
            <CameraOff className="mx-auto mb-4 text-red-400" size={48} />
            <h3 className="font-heading text-2xl font-bold text-white mb-2">Camera Access Required</h3>
            <p className="text-zinc-400 mb-6">{cameraError}</p>
            <button
              data-testid="retry-camera-button"
              onClick={startCamera}
              className="px-8 py-3 bg-cyan-500 hover:bg-cyan-400 text-black font-bold rounded-full transition-transform active:scale-95"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExercisePage;
